import {
  LockOutlined,
  SafetyCertificateFilled,
  WarningOutlined,
} from '@ant-design/icons';
import { Card, Checkbox, Form, Tooltip } from 'antd';

import { useMutation, useQuery } from '@apollo/client';
import { uniq } from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AppContext } from '../../AppContext';
import {
  OPERATIONS,
  PROJECT_DATA,
  ROUTES,
  SOURCE_TYPE,
  TOPIC_DATA,
} from '../../common/constants';
import Portal from '../../components/Portal';
import useRouter from '../../hooks/useRouter';
import ProjectBranchSelection from './components/ProjectBranchSelection';
import ProjectEnvViewTable from './components/ProjectEnvViewTable';
import TableActions from './components/TableActions';
import './dashboard.less';
import { UPDATE_ENV_KEY_VALUE } from './graphql/Mutations';
import { GET_ENVIRONMENTS, GET_KEY_VALUE_LIST } from './graphql/Queries';
import DeleteKeyModal from './modals/DeleteKeyModal';
import TwoFAModal from './modals/TwoFAModal';
import ViewKeyValueModal from './modals/ViewKeyValueModal';

const defaultColumns = [
  {
    title: 'Sr. No.',
    dataIndex: 'key',
    width: 40,
    fixed: 'left',
    // eslint-disable-next-line no-return-assign
    render: (_, __, index) => (
      // eslint-disable-next-line no-param-reassign
      <div className="custom-column">{(index += 1)}</div>
    ),
  },
  {
    title: 'Key',
    dataIndex: 'environmentKey',
    key: 'environmentKey',
    width: 240,
    fixed: 'left',
    render: (value, record) => (
      <div className="align-center gap-24">
        <div className="d-flex flex-horizontal gap-8 lock-icon" title={value}>
          <div className="text-ellipsis key-column">{value}</div>
          {record?.isPrivate && <LockOutlined />}
        </div>
      </div>
    ),
  },
];

function Dashboard() {
  const {
    navigate,
    location: { state },
  } = useRouter();
  const { setProjectTopicId, isTwoFaSet, fetchTwoFaKey } = useContext(
    AppContext,
  );
  const [privateKeyValueform] = Form.useForm();
  const isVerified = isTwoFaSet();
  const twoFaKey = fetchTwoFaKey();

  const [viewKeyValueModalOpen, setViewKeyValueModalOpen] = useState(false);
  const [twoFAModalOpen, setTwoFAModalOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedProject, setSelectedProject] = useState(
    // eslint-disable-next-line no-undef
    JSON?.parse(localStorage?.getItem(PROJECT_DATA)),
  );
  const [selectedProjectTopics, setSelectedProjectTopics] = useState(
    // eslint-disable-next-line no-undef
    JSON?.parse(localStorage?.getItem(TOPIC_DATA))?.value,
  );
  const [selectedTopics, setSelectedTopics] = useState(
    // eslint-disable-next-line no-undef
    JSON?.parse(localStorage?.getItem(TOPIC_DATA)),
  );
  const [topicId, setTopicId] = useState(null);
  const [deleteKeyModalOpen, setDeleteKeyModalOpen] = useState(false);
  const [isEnvVerified, setIsEnvVerified] = useState(false);
  const [sourceType, setSourceType] = useState(
    // eslint-disable-next-line no-undef
    localStorage?.getItem(SOURCE_TYPE) || null,
  );

  const [actionId, setActionId] = useState(null);
  const [actionKey, setActionKey] = useState(null);
  const [envColumns, setEnvColumns] = useState([...defaultColumns]);
  const [selectedItemName, setSelectedItemName] = useState('');
  const [duplicateKeys, setDuplicateKeys] = useState([]);
  const [nullValueKeys, setNullValueKeys] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState([]);

  const handleCheckboxChange = (env) => {
    setIsEnvVerified(env?.isVerified);
    setActionId((prev) => (prev === env?.id ? '' : env?.id));
    setSelectedItemName(env?.name);
  };

  const renderNewColumns = useCallback(
    (environments) =>
      environments?.map((env) => ({
        title: (
          <div className="d-flex justify-between">
            <div className="d-flex align-center gap-8 verified-env">
              <Checkbox
                checked={actionId === env?.id}
                onChange={() => handleCheckboxChange(env)}
              />
              <span className="m-0 text-left">{env?.name}</span>
              {env?.isVerified && <SafetyCertificateFilled />}
            </div>
          </div>
        ),
        dataIndex: env?.name,
        key: env?.name,
        editable: true,
        ellipsis: true,
        render: (_, record) => {
          const columnToSetValue = record?.environmentKeyValues?.find(
            (envName) => env?.name === envName?.environmentName,
          );
          const currentValue = columnToSetValue?.hash || null;

          // Group values for uniqueness check
          const groupedValues = record?.environmentKeyValues?.reduce(
            (acc, item) => {
              const keyValue = item?.hash || null;
              if (!acc[keyValue]) {
                acc[keyValue] = [];
              }
              acc[keyValue].push(item?.environmentName);
              return acc;
            },
            {},
          );

          const isUniqueValue = groupedValues[currentValue]?.length === 1;

          const tooltipContent = groupedValues[currentValue]?.map((name) => (
            <div key={name}>{name}</div>
          ));

          return (
            <div className="d-flex align-center justify-between">
              <div
                className="d-flex align-center"
                title={columnToSetValue?.environmentKeyValue}
              >
                {!isUniqueValue && currentValue && (
                  <Tooltip
                    title={tooltipContent}
                    className="duplicate-value-info"
                  >
                    <WarningOutlined className="cell-warning-icon" />
                  </Tooltip>
                )}
                {columnToSetValue?.environmentKeyValue || '-'}
              </div>
            </div>
          );
        },
      })),
    [actionId],
  );

  const { data: { getKeyValueList } = {}, loading, refetch } = useQuery(
    GET_KEY_VALUE_LIST,
    {
      skip: !selectedTopics || !selectedProjectTopics,
      variables: {
        data: {
          projectTopicId:
            sourceType === 'GITHUB'
              ? selectedTopics?.id
              : selectedProjectTopics,
          environmentIds: selectedBranch,
        },
      },
      onCompleted: (res) => {
        const keyData = res?.getKeyValueList;
        setTopicId(keyData?.projectTopicId);
        setProjectTopicId(keyData?.projectTopicId);
      },
      fetchPolicy: 'network-only',
    },
  );
  const {
    data: { environments } = {},
    loading: environmentLoading,
    refetch: environmentRefetch,
  } = useQuery(GET_ENVIRONMENTS, {
    skip: !selectedProjectTopics,
    variables: {
      where: {
        projectTopicId: selectedProjectTopics,
      },
    },
    fetchPolicy: 'network-only',
    onError() {},
  });

  useEffect(() => {
    if (isVerified && JSON.parse(isVerified)) {
      navigate(ROUTES.MAIN, {
        replace: true,
        state: {
          projectData:
            state?.projectData ||
            // eslint-disable-next-line no-undef
            JSON.parse(localStorage?.getItem(PROJECT_DATA)),
          topicsId: state?.topicsId,
        },
      });
    } else {
      navigate(ROUTES?.VERIFY, { state: twoFaKey });
    }
  }, []);

  useEffect(() => {
    if (getKeyValueList?.keys?.length > 0 && selectedProjectTopics) {
      setEnvColumns([
        ...defaultColumns,
        ...renderNewColumns(getKeyValueList?.environments),
      ]);
    } else {
      setEnvColumns([...defaultColumns]);
    }
  }, [actionId, getKeyValueList, selectedProjectTopics]);

  const [updateEnvKeyValue, { loading: updateEnvLoading }] = useMutation(
    UPDATE_ENV_KEY_VALUE,
    {
      onCompleted: async () => {
        const res = await refetch();
        const envData = res?.data?.getKeyValueList?.environments?.find(
          (item) => item?.id === actionId,
        );
        setIsEnvVerified(envData?.isVerified);
      },
      onError() {},
    },
  );

  useEffect(() => {
    if (actionKey !== OPERATIONS.DELETE_KEY) {
      const keysToPushInArray = getKeyValueList?.keys?.filter((env) =>
        env?.environmentKeyValues?.some(
          (envName) =>
            actionId === envName?.environmentId &&
            envName?.environmentKeyValue === '',
        ),
      );

      const envKeyNames = keysToPushInArray?.map((obj) => obj?.environmentKey);
      setNullValueKeys(envKeyNames);

      // Group environmentKeyValues by value to find duplicates
      const duplicateGroups = getKeyValueList?.keys?.reduce((acc, env) => {
        env?.environmentKeyValues?.forEach((envName) => {
          if (
            envName?.environmentId === actionId &&
            envName?.environmentKeyValue !== ''
          ) {
            const value = envName?.hash || null;
            if (!acc[value]) {
              acc[value] = [];
            }
            acc[value]?.push(env?.environmentKey);
          }
        });
        return acc;
      }, {});

      const duplicateKeyNames = Object?.values(duplicateGroups || {})
        ?.filter((keys) => keys?.length > 1)
        ?.flat();
      setDuplicateKeys(uniq(duplicateKeyNames) || []);
    }
  }, [actionId, actionKey, getKeyValueList]);

  return (
    <>
      <Portal portalId="header-title-content">
        <ProjectBranchSelection
          selectedProject={selectedProject}
          selectedProjectTopics={selectedProjectTopics}
          setSelectedProjectTopics={setSelectedProjectTopics}
          setSelectedProject={setSelectedProject}
          setSourceType={setSourceType}
          setSelectedTopics={setSelectedTopics}
          setTopicId={setTopicId}
          setSelectedBranch={setSelectedBranch}
          environments={environments}
          environmentLoading={environmentLoading}
          selectedBranch={selectedBranch}
          setActionId={setActionId}
        />
      </Portal>
      <div className="table-card-page">
        <Card className="ant-body-scroll">
          <div className="d-flex flex-vertical justify-between flex-1">
            <div>
              <ProjectEnvViewTable
                data={getKeyValueList?.keys}
                selectedRowKeys={selectedRowKeys}
                envColumns={envColumns}
                setSelectedRowKeys={setSelectedRowKeys}
                setTwoFAModalOpen={setTwoFAModalOpen}
                setDeleteKeyModalOpen={setDeleteKeyModalOpen}
                setActionKey={setActionKey}
                setActionId={setActionId}
                updateEnvKeyValue={updateEnvKeyValue}
                loading={loading || updateEnvLoading}
                setSelectedItemName={setSelectedItemName}
                refetch={refetch}
              />
            </div>
            <div>
              <TableActions
                data={getKeyValueList?.keys}
                setActionKey={setActionKey}
                setTwoFAModalOpen={setTwoFAModalOpen}
                selectedKeys={selectedRowKeys}
                actionId={actionId}
                setActionId={setActionId}
                actionKey={actionKey}
                topicId={topicId}
                isEnvVerified={isEnvVerified}
                nullValueKeys={nullValueKeys}
                duplicateKeys={duplicateKeys}
                selectedProject={selectedProject}
                selectedProjectTopics={selectedProjectTopics}
                viewKeyValueModalOpen={viewKeyValueModalOpen}
                sourceType={sourceType}
                selectedTopics={selectedTopics}
                refetchEnvKeyValueData={refetch}
                selectedRowKeys={selectedRowKeys}
                envRecords={getKeyValueList?.keys}
                setSelectedRowKeys={setSelectedRowKeys}
                selectedItemName={selectedItemName}
                environmentRefetch={environmentRefetch}
              />
            </div>
          </div>
        </Card>
      </div>

      <DeleteKeyModal
        visible={deleteKeyModalOpen}
        setVisible={setDeleteKeyModalOpen}
        setActionKey={setActionKey}
        setTwoFaModalVisible={setTwoFAModalOpen}
        setActionId={setActionId}
        selectedItemName={selectedItemName}
      />
      {twoFAModalOpen && (
        <TwoFAModal
          visible={twoFAModalOpen}
          setVisible={setTwoFAModalOpen}
          setValueModal={setViewKeyValueModalOpen}
          privateKeyValueform={privateKeyValueform}
          refetchEnvKeyValueData={refetch}
          actionId={actionId}
          actionKey={actionKey}
          setActionKey={setActionKey}
          setActionId={setActionId}
          selectedItemName={selectedItemName}
          environmentRefetch={environmentRefetch}
          setSelectedBranch={setSelectedBranch}
          selectedBranch={selectedBranch}
          setIsEnvVerified={setIsEnvVerified}
        />
      )}
      <ViewKeyValueModal
        visible={viewKeyValueModalOpen}
        setVisible={setViewKeyValueModalOpen}
        privateKeyValueform={privateKeyValueform}
        updateEnvKeyValue={updateEnvKeyValue}
        actionId={actionId}
        setActionId={setActionId}
        setActionKey={setActionKey}
      />
    </>
  );
}

export default Dashboard;
